import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { Button, Input } from '@atoms';
import toast from 'react-hot-toast';

interface Status {
  id: string;
  key: string;
  label: string;
  description: string | null;
  color: string | null;
  orderNum: number;
  isActive: boolean;
}

interface AdminStatusModalProps {
  status: Status | null;
  isEditing: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PREDEFINED_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#64748b', // slate
  '#6b7280', // gray
];

export const AdminStatusModal: React.FC<AdminStatusModalProps> = ({
  status,
  isEditing,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    key: '',
    label: '',
    description: '',
    color: '',
    orderNum: 0,
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load existing status data when editing
  useEffect(() => {
    if (isEditing && status) {
      setFormData({
        key: status.key,
        label: status.label,
        description: status.description || '',
        color: status.color || '',
        orderNum: status.orderNum,
        isActive: status.isActive,
      });
    }
  }, [isEditing, status]);

  // Get next order number for new status
  const { data: existingStatuses } = trpc.admin.getAllStatuses.useQuery();
  useEffect(() => {
    if (!isEditing && existingStatuses?.data) {
      const maxOrder = Math.max(0, ...existingStatuses.data.map(s => s.orderNum));
      setFormData(prev => ({ ...prev, orderNum: maxOrder + 1 }));
    }
  }, [isEditing, existingStatuses]);

  const createStatusMutation = trpc.admin.createStatus.useMutation({
    onSuccess: () => {
      toast.success('Status created successfully');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create status');
    },
  });

  const updateStatusMutation = trpc.admin.updateStatus.useMutation({
    onSuccess: () => {
      toast.success('Status updated successfully');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update status');
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    } else if (!/^[a-z0-9_-]+$/.test(formData.key)) {
      newErrors.key = 'Key must contain only lowercase letters, numbers, hyphens, and underscores';
    }

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (formData.orderNum < 0) {
      newErrors.orderNum = 'Order must be a non-negative number';
    }

    if (formData.color && !/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = 'Color must be a valid hex color (e.g., #ff0000)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      description: formData.description.trim() || undefined,
      color: formData.color.trim() || undefined,
    };

    if (isEditing && status) {
      await updateStatusMutation.mutateAsync({
        id: status.id,
        data: submitData,
      });
    } else {
      await createStatusMutation.mutateAsync(submitData);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isLoading = createStatusMutation.isPending || updateStatusMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-primary rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-primary">
            <h2 className="text-xl font-semibold text-text-primary">
              {isEditing ? 'Edit Status' : 'Create Status'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-4">
            {/* Key */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Key *
              </label>
              <Input
                type="text"
                value={formData.key}
                onChange={(e) => handleInputChange('key', e.target.value.toLowerCase())}
                placeholder="e.g., pending, approved, rejected"
                disabled={isLoading}
                error={!!errors.key}
              />
              {errors.key && (
                <p className="mt-1 text-xs text-error-600">{errors.key}</p>
              )}
              <p className="mt-1 text-xs text-text-secondary">
                Used internally. Use lowercase letters, numbers, hyphens, and underscores only.
              </p>
            </div>

            {/* Label */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Display Label *
              </label>
              <Input
                type="text"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                placeholder="e.g., Pending Review, Approved, Rejected"
                disabled={isLoading}
                error={!!errors.label}
              />
              {errors.label && (
                <p className="mt-1 text-xs text-error-600">{errors.label}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description of what this status represents"
                rows={3}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-border-primary rounded-lg text-text-primary bg-background-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-vertical"
              />
            </div>

            {/* Order Number */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Display Order *
              </label>
              <Input
                type="number"
                value={formData.orderNum}
                onChange={(e) => handleInputChange('orderNum', parseInt(e.target.value) || 0)}
                min={0}
                disabled={isLoading}
                error={!!errors.orderNum}
              />
              {errors.orderNum && (
                <p className="mt-1 text-xs text-error-600">{errors.orderNum}</p>
              )}
              <p className="mt-1 text-xs text-text-secondary">
                Lower numbers appear first in lists
              </p>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Color
              </label>
              <div className="space-y-3">
                <Input
                  type="text"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder="#3b82f6"
                  disabled={isLoading}
                  error={!!errors.color}
                />
                {errors.color && (
                  <p className="mt-1 text-xs text-error-600">{errors.color}</p>
                )}
                
                {/* Color Picker */}
                <div className="grid grid-cols-5 gap-2">
                  {PREDEFINED_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleInputChange('color', color)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === color 
                          ? 'border-text-primary scale-110' 
                          : 'border-border-primary hover:border-text-secondary'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                
                {/* Color Preview */}
                {formData.color && (
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full border border-border-primary"
                      style={{ backgroundColor: formData.color }}
                    />
                    <span className="text-sm text-text-secondary">Preview</span>
                  </div>
                )}
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 text-primary-600 border-border-primary rounded focus:ring-primary-500"
              />
              <label htmlFor="isActive" className="text-sm text-text-primary">
                Active status (can be used in the system)
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border-primary">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{isEditing ? 'Update' : 'Create'} Status</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
